import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于日漫屋',
  description: '日漫屋是聚合日漫字幕组最新译制资源的日漫汉化在线阅读平台，提供简体、繁体及双语对照阅读。',
  publisher: { '@type': 'Organization', name: '日漫屋', url: 'https://comicniche.github.io/' },
  inLanguage: 'zh-CN',
};

export default function About() {
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get('website_alt') as string)?.trim();
    if (honeypot) {
      setSuccess(true);
      form.reset();
      return;
    }
    fd.delete('website_alt');
    setSubmitting(true);
    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/da08p103256a3o8kora0', {
        method: 'POST',
        body: new URLSearchParams(fd as unknown as URLSearchParams),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? responseText;
      if (res.ok && parsed?.code === 'OK') {
        setSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setFormError('网络异常，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { icon: 'ri-book-3-line', value: '1,200+', label: '汉化作品' },
    { icon: 'ri-team-line', value: '80+', label: '合作字幕组' },
    { icon: 'ri-heart-3-line', value: '2,000万+', label: '累计阅读' },
    { icon: 'ri-flashlight-line', value: '24h', label: '持续更新' },
  ];

  const reviews = [
    {
      name: '小枫',
      role: '热血漫爱好者',
      text: '这里的双语对照功能太赞了，追星海破晓的同时还能学点日语，字幕组更新也快，追更完全不用愁！',
    },
    {
      name: '阿川',
      role: '重度追更党',
      text: '版本切换很顺畅，简体繁体一键就能换，给家里长辈看繁体也方便。资源聚合得很全，基本想看的热门连载都有。',
    },
    {
      name: 'Momo酱',
      role: '日常治愈系读者',
      text: '页面干净清爽，没有乱七八糟的广告，阅读体验在同类站里算很舒服的了，果断收藏！',
    },
  ];

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pt-24 pb-10 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        {/* Brand intro */}
        <section className="overflow-hidden rounded-lg bg-foreground-900 text-background-50">
          <div className="grid grid-cols-1 gap-8 p-8 md:grid-cols-2 md:p-12">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-4 py-1.5 text-sm text-primary-200">
                <i className="ri-book-open-line"></i> 关于日漫屋
              </span>
              <h1 className="font-heading mt-5 text-3xl font-bold md:text-4xl">让每一部好日漫，都被看见</h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-200">
                日漫屋诞生于一群日漫爱好者的共同热爱。我们聚合优质字幕组的最新译制资源，致力于为读者提供
                <strong className="text-background-50">日漫简体、繁体及双语对照</strong>
                的便捷阅读体验。热门连载同步翻译，点开即选版本，追更不迷路——这是我们对每一位读者的承诺。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-300">
                我们始终坚持「尊重创作、支持正版」的理念，所有内容仅供学习交流。希望通过我们的努力，让更多优质的日漫作品被更多人看到，也让汉化文化在热爱中延续。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#contact" className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors">
                  联系我们
                </a>
                <Link to="/category/0" className="whitespace-nowrap rounded-full border border-foreground-300/40 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-foreground-800 transition-colors">
                  开始阅读
                </Link>
              </div>
            </div>
            <div className="hidden items-center justify-center md:flex">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20anime%20illustration%20of%20readers%20enjoying%20manga%20books%20and%20tablets%20in%20a%20cozy%20library%20with%20warm%20golden%20light%20and%20floating%20comic%20panels%2C%20warm%20cream%20and%20crimson%20tones%2C%20artistic%20abstract%20style%2C%20high%20detail&width=800&height=600&seq=about-2026-x1&orientation=landscape"
                alt="日漫屋阅读文化插画"
                className="w-full rounded-lg object-cover"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mt-12" aria-labelledby="stat-title">
          <h2 id="stat-title" className="sr-only">平台数据</h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center rounded-lg border border-background-200/70 bg-background-100 p-6 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <span className="font-heading mt-4 text-3xl font-bold text-foreground-950">{s.value}</span>
                <span className="mt-1 text-sm text-foreground-600">{s.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Reputation */}
        <section className="mt-12" aria-labelledby="review-title">
          <div className="mb-6">
            <h2 id="review-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-chat-heart-line text-primary-500"></i> 读者口碑
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.name} className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-amber-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">「{r.text}」</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-sm font-semibold text-secondary-900">
                    {r.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{r.name}</p>
                    <p className="text-xs text-foreground-500">{r.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2" aria-labelledby="contact-title">
          <div>
            <h2 id="contact-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-mail-send-line text-primary-500"></i> 联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是字幕组合作、版权授权，还是阅读建议，都欢迎与我们联系。我们会尽快回复每一条留言。
            </p>
            <ul className="mt-6 space-y-4 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">商务与版权</p>
                  <p className="font-medium">contact@rimangwu.example</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-chat-3-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">字幕组合作</p>
                  <p className="font-medium">Telegram @rimangwu_group</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                  <i className="ri-map-pin-2-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">运营团队</p>
                  <p className="font-medium">日漫屋爱好者运营团队 · 在线支持</p>
                </div>
              </li>
            </ul>
          </div>

          <form
            data-readdy-form="日漫屋联系咨询表单"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200/70 bg-background-50 p-6"
          >
            {success ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <p className="mt-4 text-base font-semibold text-foreground-950">留言已发送！</p>
                <p className="mt-1 text-sm text-foreground-600">感谢你的反馈，我们会尽快与你联系。</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      您的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full rounded-md border border-background-300/70 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full rounded-md border border-background-300/70 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      placeholder="name@example.com"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full resize-none rounded-md border border-background-300/70 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    placeholder="请描述您的需求或建议（500字以内）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-honeypot"
                />
                {formError && (
                  <p className="mt-3 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">{formError}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {submitting ? '发送中…' : '发送留言'}
                </button>
              </>
            )}
          </form>
        </section>
      </main>

      <Footer />
    </>
  );
}